import { memo, useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, ShoppingCart, User, Clock, Coins } from "lucide-react";
import type { TokenInfo } from "../hooks/useSquadToken";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatMon(wei: bigint): string {
  if (wei === 0n) return "0";
  const eth = Number(wei) / 1e18;
  if (eth < 0.0001) return "<0.0001";
  if (eth < 1) return eth.toFixed(6);
  if (eth < 1000) return eth.toFixed(4);
  return (eth / 1000).toFixed(2) + "K";
}

function formatSupply(wei: bigint): string {
  const n = Number(wei) / 1e18;
  if (n >= 1_000_000_000) return (n / 1_000_000_000).toFixed(2) + "B";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(2) + "K";
  return n.toLocaleString();
}

function timeAgo(ts: number): string {
  const diff = Math.floor(Date.now() / 1000) - ts;
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

/** Resolve ipfs:// URIs to a public gateway URL. base64 and https pass through unchanged. */
function resolveImage(uri: string): string {
  if (!uri) return "";
  if (uri.startsWith("ipfs://")) {
    return uri.replace("ipfs://", "https://gateway.pinata.cloud/ipfs/");
  }
  return uri;
}

/** Generate a deterministic gradient from a token symbol string */
function symbolGradient(symbol: string): string {
  let hash = 0;
  for (let i = 0; i < symbol.length; i++) {
    hash = symbol.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue1 = Math.abs(hash) % 360;
  const hue2 = (hue1 + 60) % 360;
  return `linear-gradient(135deg, hsl(${hue1},80%,50%), hsl(${hue2},80%,60%))`;
}

function soldPercent(info: TokenInfo): number {
  if (info.totalSupply === 0n) return 0;
  return Number((info.tokensSold * 10000n) / (info.totalSupply / 10n ** 18n)) / 100;
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface TokenCardProps {
  token: TokenInfo;
  onBuy: (token: TokenInfo) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const TokenCard = memo(function TokenCard({ token, onBuy }: TokenCardProps) {
  const [imgErr, setImgErr] = useState(false);
  const pct  = soldPercent(token);
  const grad = symbolGradient(token.symbol);
  const src  = resolveImage(token.imageURI);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className="card-glass glow-border rounded-2xl overflow-hidden flex flex-col group hover:glow-border-strong transition-all duration-300"
    >
      {/* ── Token Header ── */}
      <div className="relative h-28 overflow-hidden">
        {src && !imgErr ? (
          <img
            src={src}
            alt={token.name}
            onError={() => setImgErr(true)}
            className="w-full h-full object-cover"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center text-4xl font-display tracking-wider"
            style={{ background: grad }}
          >
            {token.symbol.slice(0, 3)}
          </div>
        )}
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-[--color-card] via-transparent to-transparent" />
        {/* Symbol badge */}
        <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm border border-[--color-border] rounded-lg px-2 py-0.5">
          <span className="font-mono text-xs text-[--color-pink] font-bold">{token.symbol}</span>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="p-4 flex flex-col gap-3 flex-1">
        {/* Name + creator */}
        <div>
          <h3 className="font-display text-lg text-white tracking-wide leading-tight truncate">
            {token.name}
          </h3>
          <div className="flex items-center gap-1 mt-0.5">
            <User size={10} className="text-[--color-pink]" />
            <span className="font-mono text-xs text-[--color-pink]/80 truncate">
              {token.creatorDomain}
            </span>
          </div>
        </div>

        {/* Description */}
        {token.description && (
          <p className="text-xs text-white/50 font-body line-clamp-2 leading-relaxed">
            {token.description}
          </p>
        )}

        {/* Stats grid */}
        <div className="grid grid-cols-2 gap-2">
          <Stat label="Price"   value={`${formatMon(token.currentPrice ?? 0n)} MON`} icon={<TrendingUp size={10} />} />
          <Stat label="Mkt Cap" value={`${formatMon(token.marketCap   ?? 0n)} MON`} icon={<Coins size={10} />} />
          <Stat label="Supply"  value={formatSupply(token.totalSupply)}              icon={<Coins size={10} />} />
          <Stat label="Launched" value={timeAgo(token.createdAt)}                   icon={<Clock size={10} />} />
        </div>

        {/* Bonding curve progress */}
        <div>
          <div className="flex justify-between items-center mb-1">
            <span className="text-[10px] text-white/40 font-body">Bonding Progress</span>
            <span className="text-[10px] font-mono text-[--color-pink]">{pct.toFixed(1)}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${Math.min(pct, 100)}%` }}
              transition={{ duration: 0.8, ease: "easeOut", delay: 0.1 }}
              className="h-full rounded-full bg-gradient-to-r from-[--color-pink] to-purple-500"
            />
          </div>
        </div>

        {/* Buy button */}
        <button
          onClick={() => onBuy(token)}
          className="btn-pink w-full py-2 text-sm flex items-center justify-center gap-2 mt-auto"
        >
          <ShoppingCart size={14} />
          Buy {token.symbol}
        </button>
      </div>
    </motion.div>
  );
});

// ─── Sub-component ────────────────────────────────────────────────────────────

function Stat({ label, value, icon }: { label: string; value: string; icon: React.ReactNode }) {
  return (
    <div className="bg-white/5 rounded-lg px-2.5 py-1.5">
      <div className="flex items-center gap-1 mb-0.5 text-white/40">
        {icon}
        <span className="text-[10px] font-body">{label}</span>
      </div>
      <span className="text-xs font-mono text-white/90 font-semibold">{value}</span>
    </div>
  );
}

export default TokenCard;